import { useState, type MouseEvent } from "react";
import { CalendarPlus, Download, Eye, EyeOff, FileImage, FileSpreadsheet, FileText, FolderLock, Paperclip } from "lucide-react";
import { AgendaIcsBanner } from "@/features/message/AgendaIcsBanner";
import { AgendaIcsImportModal } from "@/features/message/AgendaIcsImportModal";
import { CoffreDestFolderPickerModal } from "@/features/message/CoffreDestFolderPickerModal";
import { downloadMailAttachment } from "@/lib/api/imap";
import {
  getAttachmentPreviewKind,
  isCalendarInviteAttachment,
  isPreviewableAttachment,
  isTextAttachment,
} from "@/lib/mail/attachmentPreview";
import {
  downloadAllMailAttachments,
  saveAllMailAttachmentsToCoffre,
  saveMailAttachmentToCoffre,
} from "@/lib/mail/saveMailAttachmentToSuite";
import { prefetchCoffrePickerList, type CoffreListZone } from "@/lib/suite/coffreSuiteApi";
import { cn } from "@/lib/cn";
import type { MailAttachment } from "@/lib/types/mail";

function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

type Props = {
  accountId: number;
  folder: string;
  messageUid: number;
  attachments: MailAttachment[];
  previewPart?: string | null;
  onPreview?: (file: MailAttachment | null) => void;
};

type SuitePicker =
  | { kind: "coffre"; mode: "one"; file: MailAttachment }
  | { kind: "coffre"; mode: "all" };

export function MessageAttachments({
  accountId,
  folder,
  messageUid,
  attachments,
  previewPart,
  onPreview,
}: Props) {
  const [busyPart, setBusyPart] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [picker, setPicker] = useState<SuitePicker | null>(null);
  const [icsFile, setIcsFile] = useState<MailAttachment | null>(null);

  const showBulkBar = attachments.length > 1;
  const busy = bulkBusy || busyPart != null;

  if (attachments.length === 0) {
    return null;
  }

  const location = { accountId, folder, messageUid };

  async function handleDownload(file: MailAttachment, e?: MouseEvent) {
    e?.stopPropagation();
    setError(null);
    setSuccess(null);
    setBusyPart(file.part);
    try {
      await downloadMailAttachment({
        accountId,
        folder,
        uid: messageUid,
        part: file.part,
        filename: file.filename,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Téléchargement impossible.");
    } finally {
      setBusyPart(null);
    }
  }

  function handleClick(file: MailAttachment) {
    if (!isPreviewableAttachment(file) || !onPreview) {
      void handleDownload(file);
      return;
    }

    if (previewPart === file.part) {
      onPreview(null);
      return;
    }

    onPreview(file);
  }

  async function handleDownloadAll() {
    setError(null);
    setSuccess(null);
    setBulkBusy(true);
    setBulkProgress(`0 / ${attachments.length}`);
    try {
      const result = await downloadAllMailAttachments(location, attachments, (done, total) => {
        setBulkProgress(`${Math.min(done + 1, total)} / ${total}`);
      });
      if (result.ok === 0) {
        setError("Aucune pièce jointe téléchargée.");
        return;
      }
      setSuccess(
        `${result.ok} pièce(s) jointe(s) téléchargée(s).${
          result.failed ? ` ${result.failed} échec(s).` : ""
        }`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Téléchargement impossible.");
    } finally {
      setBulkBusy(false);
      setBulkProgress(null);
    }
  }

  async function handleSaveCoffre(folderPath: string, zone: CoffreListZone) {
    if (!picker || picker.kind !== "coffre") return;
    setError(null);
    setSuccess(null);
    setBulkBusy(true);
    try {
      if (picker.mode === "one") {
        const result = await saveMailAttachmentToCoffre(location, picker.file, folderPath, zone);
        if (!result.ok) {
          setError(result.error ?? "Enregistrement Coffre impossible.");
          return;
        }
        setSuccess(`« ${picker.file.filename} » enregistré dans le Coffre.`);
      } else {
        setBulkProgress(`0 / ${attachments.length}`);
        const result = await saveAllMailAttachmentsToCoffre(
          location,
          attachments,
          folderPath,
          zone,
          (done, total) => setBulkProgress(`${Math.min(done + 1, total)} / ${total}`)
        );
        if (result.ok === 0) {
          setError("Aucune pièce jointe enregistrée dans le Coffre.");
          return;
        }
        setSuccess(
          `${result.ok} pièce(s) jointe(s) enregistrée(s) dans le Coffre.${
            result.failed ? ` ${result.failed} échec(s).` : ""
          }`
        );
      }
      setPicker(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement Coffre impossible.");
    } finally {
      setBulkBusy(false);
      setBulkProgress(null);
    }
  }

  const calendarFiles = attachments.filter(isCalendarInviteAttachment);
  const coffreOpen = picker?.kind === "coffre";
  const coffreFilename =
    picker?.kind === "coffre" && picker.mode === "one"
      ? picker.file.filename
      : picker?.kind === "coffre"
        ? `${attachments.length} fichiers`
        : undefined;

  return (
    <>
      {calendarFiles.length > 0 ? (
        <div className="mail-ics-banners">
          {calendarFiles.map((file) => (
            <AgendaIcsBanner
              key={`${file.part}-${file.filename}`}
              accountId={accountId}
              folder={folder}
              messageUid={messageUid}
              file={file}
              onAdd={() => {
                setError(null);
                setSuccess(null);
                setIcsFile(file);
              }}
            />
          ))}
        </div>
      ) : null}
      <div className="shrink-0 border-b border-[var(--mail-border-soft)] bg-[var(--mail-surface-hover)]/60 px-3 py-1.5">
        {showBulkBar ? (
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-[10px] font-medium uppercase tracking-wide text-[var(--mail-muted)]">
              {attachments.length} PJ
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleDownloadAll()}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-2 py-1 text-[11px] font-medium text-[var(--mail-text)] transition hover:border-[var(--mail-accent)] disabled:opacity-50"
            >
              <Download className="h-3 w-3" aria-hidden />
              Tout télécharger
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                setSuccess(null);
                prefetchCoffrePickerList("");
                setPicker({ kind: "coffre", mode: "all" });
              }}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-2 py-1 text-[11px] font-medium text-[var(--mail-text)] transition hover:border-[var(--mail-accent)] disabled:opacity-50"
            >
              <FolderLock className="h-3 w-3" aria-hidden />
              Tout → Coffre
            </button>
            {bulkProgress ? (
              <span className="text-[10px] tabular-nums text-[var(--mail-muted)]">{bulkProgress}</span>
            ) : null}
          </div>
        ) : null}

        <ul className="flex flex-wrap gap-2">
          {attachments.map((file) => {
            const previewKind = getAttachmentPreviewKind(file);
            const previewable = previewKind != null;
            const selected = previewPart === file.part;
            const fileBusy = busyPart === file.part;
            const calendarFile = isCalendarInviteAttachment(file);

            return (
              <li key={`${file.part}-${file.filename}`}>
                <div
                  className={cn(
                    "inline-flex max-w-full items-center rounded-full border shadow-[var(--mail-shadow-sm)] transition-all",
                    selected
                      ? "border-[var(--mail-accent)] bg-[var(--mail-accent-soft)] ring-1 ring-[var(--mail-accent)]/30"
                      : "border-[var(--mail-border-soft)] bg-[var(--mail-surface)] hover:border-[var(--mail-accent)]"
                  )}
                >
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleClick(file)}
                    className="inline-flex min-w-0 items-center gap-2 px-3 py-1.5 text-xs text-[var(--mail-text)] disabled:opacity-50"
                    title={
                      selected
                        ? `Fermer l’aperçu de ${file.filename}`
                        : previewable
                          ? `Aperçu de ${file.filename}`
                          : `Télécharger ${file.filename}`
                    }
                  >
                    {previewKind === "pdf" ? (
                      <FileText className="h-3.5 w-3.5 shrink-0 text-red-600" aria-hidden />
                    ) : previewKind === "image" ? (
                      <FileImage className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />
                    ) : previewKind === "text" || isTextAttachment(file) ? (
                      <FileText className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
                    ) : previewKind === "office" ? (
                      <FileSpreadsheet className="h-3.5 w-3.5 shrink-0 text-indigo-600" aria-hidden />
                    ) : (
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-[var(--mail-accent)]" aria-hidden />
                    )}
                    <span className="truncate">{file.filename}</span>
                    <span className="shrink-0 text-[var(--mail-muted)]">
                      {formatAttachmentSize(file.size)}
                    </span>
                    {previewable ? (
                      selected ? (
                        <EyeOff className="h-3.5 w-3.5 shrink-0 text-[var(--mail-accent)]" aria-hidden />
                      ) : (
                        <Eye className="h-3.5 w-3.5 shrink-0 text-[var(--mail-muted)]" aria-hidden />
                      )
                    ) : null}
                  </button>

                  <div className="flex shrink-0 items-center border-l border-[var(--mail-border-soft)]">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={(e) => void handleDownload(file, e)}
                      className="message-attach-action-btn px-2 py-1.5"
                      aria-label={`Télécharger ${file.filename}`}
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    {calendarFile ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          setError(null);
                          setSuccess(null);
                          setIcsFile(file);
                        }}
                        className="message-attach-action-btn px-2 py-1.5"
                        aria-label={`Enregistrer ${file.filename} dans l’agenda`}
                        title="Enregistrer dans l’agenda"
                      >
                        <CalendarPlus className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={(e) => {
                        e.stopPropagation();
                        setError(null);
                        setSuccess(null);
                        prefetchCoffrePickerList("");
                        setPicker({ kind: "coffre", mode: "one", file });
                      }}
                      className="message-attach-action-btn rounded-r-full px-2 py-1.5 pr-2.5"
                      aria-label={`Enregistrer ${file.filename} dans Coffre`}
                    >
                      <FolderLock className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    {fileBusy ? (
                      <span className="sr-only">En cours…</span>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {success ? (
          <p className="mt-2 text-xs text-[var(--mail-accent)]" role="status">
            {success}
          </p>
        ) : null}
        {error ? (
          <p className="mt-2 text-xs text-[var(--mail-danger-text)]" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <CoffreDestFolderPickerModal
        open={coffreOpen}
        filename={coffreFilename}
        title={
          picker?.kind === "coffre" && picker.mode === "all"
            ? `Enregistrer ${attachments.length} pièce(s) jointe(s) dans le Coffre`
            : undefined
        }
        onClose={() => {
          if (!bulkBusy) setPicker(null);
        }}
        onConfirm={handleSaveCoffre}
      />
      <AgendaIcsImportModal
        open={icsFile != null}
        filename={icsFile?.filename ?? ""}
        location={location}
        file={icsFile}
        onClose={() => setIcsFile(null)}
        onDone={(message) => {
          setIcsFile(null);
          setSuccess(message);
        }}
      />
    </>
  );
}
